// Types
import type { ReactNode } from 'react';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import Image from 'next/image';

import useTranslation from '@/utils/hooks/translation/useTranslation';
import useCurrentSession from '@/utils/hooks/user/useCurrentSession';

// Hooks
import useDarkMode from '@/utils/hooks/utils/useDarkMode';

// Routes
import { SIGN_IN } from '@/lib/routes/publicRoutes';
// Lib
import { reassuranceLogosAwards } from '@/lib/signup/reassuranceLogos';

import Divider from '@/components/base/Divider/Divider';
import { NO_BACKGROUND_BLACK } from '@/components/base/Dropdown';
// Components
import H1 from '@/components/base/Headings/H1';
import LinkBase from '@/components/base/LinkBase';
import LoaderPage from '@/components/base/Loader/LoaderPage';
import Iconosquare from '@/components/icons/Iconosquare';
import SwitchLanguage from '@/components/pages/signup/SwitchLanguage';
import TrustBadge from '@/components/pages/signup/TrustBadge';
import { Trans } from '@/components/tools/translation';

// Images
import illustration from './../../../public/images/signup/dashboard-preview.webp';

type LayoutSignUpPublicProps = {
    children: ReactNode;
    titleLocale: string;
    subTitleLocale?: string;
    headerChildren?: ReactNode;
    displayFooter?: boolean;
};

const LayoutSignUpPublic = ({
    children,
    titleLocale,
    subTitleLocale,
    headerChildren,
    displayFooter = true
}: LayoutSignUpPublicProps) => {
    const { t } = useTranslation('signup_funnel');
    const { user, isLoading } = useCurrentSession({ isLoggedRedirect: true });
    const { useRemoveDarkMode } = useDarkMode();

    useRemoveDarkMode();
    if (isLoading || user) {
        return <LoaderPage />;
    }

    return (
        <GoogleReCaptchaProvider reCaptchaKey={process.env.NEXT_PUBLIC_RECAPTCHA_PUBLIC_KEY}>
            <div className="flex h-screen w-screen flex-col overflow-y-auto overflow-x-hidden bg-white">
                {/* Mobile + Desktop: 2 stacked columns on mobile, side by side on desktop */}
                <div className="flex flex-1 flex-col lg:flex-row">
                    {/* Left Column - Form with Logo */}
                    <div className="flex w-full flex-col content-center items-center bg-icoGray-100 px-10 py-8 lg:w-2/5 lg:px-28 lg:pb-10 lg:pt-20">
                        {/* Mobile only: Language Switcher + Logo */}
                        <div className="mb-8 flex w-full shrink-0 items-center justify-between lg:hidden">
                            <Iconosquare size={37} className="text-icoBlue" />
                            <SwitchLanguage buttonStyle={NO_BACKGROUND_BLACK} />
                        </div>

                        {/* Desktop only: Logo only */}
                        <div className="mb-8 hidden w-full max-w-[350px] shrink-0 lg:block">
                            <Iconosquare size={37} className="text-icoBlue" />
                        </div>

                        {/* Main Content */}
                        <div className="flex h-full w-full flex-col items-center justify-center gap-6">
                            <div className="w-full max-w-[350px]">
                                {headerChildren}
                                <H1 className="mb-6 font-GBold text-4xl text-icoBlack lg:text-5xl">
                                    <Trans
                                        i18nKey={'signup_funnel:create_account.' + titleLocale}
                                        components={{ br: <br /> }}
                                    />
                                </H1>
                                {subTitleLocale ? (
                                    <p className="mb-10 font-GMedium text-base text-icoGray-500">
                                        <Trans
                                            i18nKey={
                                                'signup_funnel:create_account.' + subTitleLocale
                                            }
                                            components={{ br: <br /> }}
                                        />
                                    </p>
                                ) : null}
                                {children}
                                {displayFooter ? (
                                    <>
                                        <Divider className="my-8">
                                            <span className="text-sm text-icoGray-400">
                                                {t('commons:or')}
                                            </span>
                                        </Divider>
                                        <div className="text-center font-GSemiBold text-sm">
                                            <Trans
                                                i18nKey="signup_funnel:create_account.already_have_account"
                                                components={{
                                                    tag0: (
                                                        <LinkBase
                                                            key="login"
                                                            href={SIGN_IN}
                                                            className="text-icoBlue"
                                                        />
                                                    )
                                                }}
                                            />
                                        </div>
                                    </>
                                ) : null}
                            </div>
                        </div>
                    </div>

                    {/* Right Column - Dashboard with Language Switcher */}
                    <div className="flex w-full flex-col overflow-hidden bg-white p-8 lg:w-3/5 lg:pb-10 lg:pt-20">
                        {/* Desktop only: Language Switcher */}
                        <div className="mb-8 hidden shrink-0 justify-end lg:flex">
                            <SwitchLanguage buttonStyle={NO_BACKGROUND_BLACK} />
                        </div>

                        {/* Dashboard Preview */}
                        <div className="relative flex flex-col items-center gap-6">
                            {/* Gradient Background (behind dashboard) - positioned absolutely */}
                            <div className="absolute left-1/2 top-[160px] h-[469px] w-[690px] -translate-x-1/2">
                                <div className="absolute inset-x-0 inset-y-[-64%] bg-icoPricingPlansLightGradient opacity-60 blur-[100px]" />
                            </div>

                            {/* Dashboard Container */}
                            <div className="relative w-full overflow-hidden">
                                <Image
                                    alt="Iconosquare Dashboard Preview"
                                    width={690}
                                    height={416}
                                    src={illustration}
                                    className="h-full w-full object-cover"
                                    placeholder="blur"
                                />
                            </div>

                            {/* Trust Badge & Awards */}
                            <div className="z-1 flex flex-col items-center gap-6">
                                <TrustBadge />
                                <ul className="flex w-fit flex-wrap justify-center gap-4">
                                    {reassuranceLogosAwards.map((logo) => (
                                        <li key={logo.alt}>
                                            <Image
                                                alt={logo.alt}
                                                src={logo.image}
                                                height={107}
                                                placeholder="blur"
                                            />
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </GoogleReCaptchaProvider>
    );
};

export default LayoutSignUpPublic;
